@let notifications = (notifications$ | async) ?? [];
@let travelersAndServices = (travelersAndServices$ | async) ?? [];
@let flightOrBound = flightOrBound$ | async;
@let passengersThatAreBlockedFromSelecting = passengersThatAreBlockedFromSelecting$ | async;

<div
  class="service-selection flex-1"
  [attr.data-testid]="'service-selection-segment-id-' + flightOrBound.id"
  [attr.data-boundid]="flightOrBound.id"
>
  @switch (state$ | async) {
    <!-- CASE 1/3: SHOW LOADING INDICATOR -->
    @case (SelectionState.LOADING) {
      <fcom-loader [loaderType]="LoaderTheme.EMBEDDED" />
    }

    <!-- CASE 2/3: SHOW SERVICE OPTIONS -->
    @case (SelectionState.HAS_SERVICES) {
      @if (showSportNotification$ | async) {
        <fcom-notification
          class="block pr-medium-b"
          [theme]="NotificationTheme.WARNING"
          data-testid="sport-notification"
        >
          {{ 'ancillaries.baggage.sportEquipmentNotIncluded' | finLocalization }}
        </fcom-notification>
      }
      @for (notification of notifications; track notification.key; let last = $last) {
        <fcom-notification
          [attr.data-testid]="'service-notification-' + notification.theme"
          [class.pr-medium-b]="last"
          [class.pr-xxsmall-b]="!last"
          [theme]="notification.theme"
          [iconName]="notification.iconName"
        >
          @if (notification.key | finHasLocalizationKey) {
            <span>
              {{ notification.key | finLocalization }}
            </span>
          }
          @if (notification.reason) {
            <span>
              {{ notification.reason.key | finLocalization: notification.reason.data }}
            </span>
          }
        </fcom-notification>
      }

      @if ((blockingNotifications$ | async).length === 0) {
        @if (additionalText) {
          <fin-additional-info [additionalInfoKey$]="additionalText | finToObservable" />
        }

        <fin-tab-header
          [tabGroupId]="serviceTabGroupId"
          [startWith$]="initialTravelerIdSelected$"
          data-hj-suppress
          data-dd-privacy="hidden"
        >
          @for (iterable of travelersAndServices; track iterable.traveler.id) {
            <fin-tab-label
              [tabId]="iterable.traveler.id"
              [title]="'-'"
              [subTitle]="iterable.traveler.firstName + ' ' + iterable.traveler.lastName"
              [checked]="iterable.hasServiceSelected$ | async"
            />
          }
        </fin-tab-header>

        <div [formGroup]="form" class="pr-small-b">
          <fin-tab-group
            [tabGroupId]="serviceTabGroupId"
            class="block white-bg surface-primary border-left border-right border-bottom rounded-bottom-large"
          >
            @for (iterable of travelersAndServices; track iterable.traveler.id) {
              <fin-tab [tabId]="iterable.traveler.id">
                <fin-ancillary-selection
                  [groupedServices]="iterable.combinedGroupedServices"
                  [showRemove]="!!form.controls[iterable.traveler.id].controls['radio']"
                  [hideSelection]="iterable.services.length === 0"
                  [ancillaries]="iterable.selectedServices$ | async"
                  [serviceToVariantTierPricesMap]="
                    (travelerToServiceVariantTierPricesMap$ | async)?.get(iterable.traveler.id)
                  "
                  [isHighlightedServicesAvailable]="isHighlightedServicesAvailable"
                  (removeSelections)="removeSelections(iterable.traveler.id, flightOrBound.id)"
                />

                @if (iterable.filters.length > 0) {
                  <div class="pr-small-x">
                    @if (iterable.filters.length > 1) {
                      <div>
                        @for (filter of iterable.filters; track filter; let last = $last) {
                          <fcom-chip
                            class="inline-block mr-small-b"
                            [class.ms-xsmall-r]="!last"
                            [text]="'ancillaries.filters.' + filter + '.text' | finLocalization"
                            [title]="'ancillaries.filters.' + filter + '.ariaText' | finLocalization"
                            [theme]="
                              (iterable.selectedFilter$ | async) === filter ? ChipTheme.ACTIVE : ChipTheme.DEFAULT
                            "
                            (tagClicked)="iterable.selectedFilter$.next(filter)"
                          />
                        }
                      </div>
                    }
                    @if (iterable.filters.length === 1) {
                      <div [class.pr-small-b]="(iterable.selectedFilter$ | async) !== 'none'">
                        @if (iterable.selectedFilter$ | async; as filter) {
                          <button
                            class="font-body-1 nordic-blue-900-text text-icon-primary underline font-body-medium"
                            data-testid="special-diet-toggle"
                            (click)="
                              filter !== 'none'
                                ? iterable.selectedFilter$.next('none')
                                : iterable.selectedFilter$.next('all')
                            "
                          >
                            {{
                              filter === 'none'
                                ? ('ancillaries.show.specialDiet' | finLocalization)
                                : ('ancillaries.hide.specialDiet' | finLocalization)
                            }}
                          </button>
                        }
                      </div>
                    }
                  </div>
                }
                <div class="rounded-bottom-large pr-small pr-clear-t" [formGroup]="form.controls[iterable.traveler.id]">
                  <div class="services">
                    @if (iterable.services.length === 0 && (iterable.selectedFilter$ | async) !== 'none') {
                      <fcom-notification data-testid="pax-no-services-notification">
                        {{ 'ancillaries.noServicesForPax' | finLocalization }}
                      </fcom-notification>
                    }

                    @if (passengersHaveOverlappingBundles$ | async | finMap: iterable.traveler.id) {
                      <fcom-notification
                        data-testid="bundle-already-selected-notification"
                        class="ps-small-y border-top"
                        [theme]="NotificationTheme.WARNING"
                      >
                        {{ 'ancillaries.bundle.serviceIncludedInBundle' | finLocalization }}
                      </fcom-notification>
                    }

                    @for (service of iterable.services; track service.globalId; let last = $last) {
                      @if (
                        (service.subCategory === (iterable.selectedFilter$ | async) ||
                          (iterable.selectedFilter$ | async) === 'all') &&
                        service.category !== Category.SEAT_WIFI
                      ) {
                        <div
                          class="service block border rounded-large white-bg surface-primary overflow-hidden"
                          [class.mr-xsmall-b]="!last"
                          [class.product-selected]="service.selected$ | async"
                        >
                          @if (service.inputType === 'radio') {
                            <input
                              [id]="service.globalId + '-field'"
                              [attr.aria-describedby]="service.globalId + '-label'"
                              type="radio"
                              [value]="service.variant"
                              [attr.name]="flightOrBound.id + '-' + iterable.traveler.id"
                              [formControlName]="'radio'"
                              [attr.disabled]="
                                !!(passengersHaveOverlappingBundles$ | async | finMap: iterable.traveler.id) ||
                                (service.quota$ | finMap: 'type' | async) === QuotaType.NO ||
                                passengersThatAreBlockedFromSelecting.includes(iterable.traveler.id) ||
                                (service.paxLevelQuotaReached$ | async) ||
                                (service.paidQuantity !== 0 &&
                                  (service.totalPrice?.amount !== '0' ||
                                    service.subCategory !== SubCategory.SPECIAL_DIET))
                                  ? 'disabled'
                                  : undefined
                              "
                              (click)="clickInput($event, service, flightOrBound.id, iterable.traveler.id)"
                              class="is-vishidden"
                              data-testid="product-input"
                            />
                          }
                          @if (service.inputType === 'checkbox') {
                            <input
                              [id]="service.globalId + '-field'"
                              [attr.aria-describedby]="service.globalId + '-label'"
                              type="checkbox"
                              [value]="service.variant"
                              [attr.name]="flightOrBound.id + '-' + iterable.traveler.id"
                              [formControlName]="service.variant"
                              [attr.disabled]="
                                !!(passengersHaveOverlappingBundles$ | async | finMap: iterable.traveler.id) ||
                                (service.quota$ | finMap: 'type' | async) === QuotaType.NO ||
                                passengersThatAreBlockedFromSelecting.includes(iterable.traveler.id) ||
                                (service.paxLevelQuotaReached$ | async) ||
                                (service.paidQuantity !== 0 && service.totalPrice?.amount !== '0')
                                  ? 'disabled'
                                  : undefined
                              "
                              (click)="clickInput($event, service, flightOrBound.id, iterable.traveler.id)"
                              class="is-vishidden"
                              data-testid="product-input"
                            />
                          }
                          <label
                            #label
                            [class.hidden]="service.inputType === 'number'"
                            [attr.for]="service.globalId + '-field'"
                            [attr.aria-labelledby]="service.globalId + '-label'"
                          ></label>
                          <div class="flex service-container">
                            @if (service.media) {
                              <fin-content-image
                                class="flex-1 flex--basis0 relative"
                                [fillParent]="(aspectRatio$ | async) === '1x1'"
                                [scale]="false"
                                [imageData$]="service.media | finToObservable"
                                [aspectRatio$]="aspectRatio$"
                              />
                            }
                            <div
                              class="flex-1 flex--basis0 flex flex--column flex--space-between ps-medium nordic-blue-900-text text-icon-primary font-body-1 font-body-medium"
                            >
                              <div
                                class="flex"
                                [ngClass]="{
                                  'flex--column flex--space-between grow':
                                    service.media || (this.isTabletDown$ | async) === true,
                                }"
                              >
                                <div
                                  [ngClass]="{
                                    'flex-2 flex--basis0': !service.media && (this.isTabletDown$ | async) !== true,
                                  }"
                                >
                                  <div [id]="service.globalId + '-label'">
                                    <h3 class="font-heading-5 font-title-small">
                                      {{ service.translations.title }}
                                    </h3>
                                    <div class="flex flex--middle ps-xxsmall-t">
                                      @if (service.originalPrice) {
                                        <span
                                          class="font-body-2 strike-through font-body-small"
                                          data-testid="product-selection-original-price"
                                        >
                                          {{
                                            service.originalPrice
                                              | finPrice
                                                : {
                                                    roundPrice:
                                                      service.originalPrice && service.originalPrice.amount === '0',
                                                  }
                                          }}
                                        </span>
                                      }
                                      @if (
                                        !!(travelerToServiceVariantTierPricesMap$ | async)
                                          ?.get(iterable.traveler.id)
                                          ?.get(service.variant) === false
                                      ) {
                                        <div class="flex flex--middle">
                                          <span
                                            class="service-price flex-1 font-heading-5 heather-700-text text-icon-primary font-title-small"
                                            [class.ps-xxsmall-l]="service.originalPrice"
                                            data-testid="product-selection-total-price"
                                          >
                                            {{
                                              service.totalPrice
                                                | finPrice
                                                  : {
                                                      roundPrice:
                                                        service.totalPrice && service.totalPrice.amount === '0',
                                                    }
                                            }}
                                          </span>
                                          @if (service.totalPoints) {
                                            <span
                                              class="font-body-1 medium-type ps-xxsmall-l flex flex--middle font-label-medium"
                                              data-testid="product-selection-avios-price"
                                            >
                                              /
                                              <fcom-icon
                                                class="icon-small ms-xxsmall-x"
                                                [name]="SvgLibraryIcon.AVIOS_CURRENCY"
                                              />
                                              {{ service.totalPoints.amount | finPoints }}
                                              {{ 'ancillaries.benefits.labels.plusPoints' | finLocalization }}
                                            </span>
                                          }
                                        </div>
                                      }
                                    </div>

                                    @if (service.originalPrice) {
                                      <fcom-tag data-testid="product-selection-product-tag" [theme]="TagTheme.POPULAR">
                                        <fcom-icon class="icon-small" [name]="SvgLibraryIcon.DISCOUNT_BLOCK" />
                                        <span class="ms-xxsmall-l">{{
                                          (isPersonalizedMealOffer
                                            ? 'campaign.personalizedMealOffer'
                                            : 'campaign.badge'
                                          ) | finLocalization
                                        }}</span>
                                      </fcom-tag>
                                    }
                                  </div>

                                  <div class="ms-medium-y">
                                    @if (service.category !== Category.MEAL) {
                                      <div
                                        class="service-description"
                                        [innerHTML]="service.translations.description"
                                      ></div>
                                    }
                                  </div>
                                </div>
                                @if (
                                  !passengersThatAreBlockedFromSelecting.includes(iterable.traveler.id) &&
                                  [QuotaType.FREE, QuotaType.SELECTED].includes(service.quota$ | finMap: 'type' | async)
                                ) {
                                  <div
                                    class="selection-container fill flex--self-end"
                                    [ngClass]="{
                                      'ps-large-l flex-1 flex--basis0':
                                        !service.media && (this.isTabletDown$ | async) !== true,
                                      'ps-small-t': service.media || (this.isTabletDown$ | async) !== true,
                                    }"
                                  >
                                    @if (service.inputType === 'number') {
                                      <fin-plus-minus-selector
                                        *finLet="
                                          (travelerToServiceVariantTierPricesMap$ | async)
                                            .get(iterable.traveler.id)
                                            ?.get(service.variant) as variantTieredPrice
                                        "
                                        [attr.data-testid]="'minus-plus-' + service.globalId"
                                        [min]="service.minNumberItems"
                                        [max]="
                                          (service.paxLevelQuotaReached$ | async)
                                            ? +form.controls[iterable.traveler.id].controls[service.variant].value
                                            : service.maxNumberItems - service.paidQuantity
                                        "
                                        [title]="
                                          'ancillaries.piece'
                                            | finPluralLocalization
                                              : +form.controls[iterable.traveler.id].controls[service.variant].value
                                        "
                                        [description]="
                                          variantTieredPrice?.accumulatedPrice ?? (service.displayTotalPrice$ | async)
                                            | finPrice
                                        "
                                        [priceDescription]="
                                          variantTieredPrice?.currentProductPrice
                                            ? {
                                                text:
                                                  (+form.controls[iterable.traveler.id].controls[service.variant]
                                                    .value === 0
                                                    ? 'productSelection.tierPrice.addProduct.'
                                                    : 'productSelection.tierPrice.nextProduct.') + service.variant
                                                  | finLocalization,
                                                price: variantTieredPrice.currentProductPrice,
                                                points: variantTieredPrice.currentProductPriceAsPoints,
                                              }
                                            : null
                                        "
                                        [model]="+form.controls[iterable.traveler.id].controls[service.variant].value"
                                        [decreaseLabel]="
                                          'ancillaries.state.remove'
                                            | finLocalization: { product: service.translations.title }
                                        "
                                        [increaseLabel]="
                                          'ancillaries.state.add'
                                            | finLocalization: { product: service.translations.title }
                                        "
                                        [controls]="service.globalId + '-label'"
                                        (increment)="addQuantity(service, flightOrBound.id, iterable.traveler.id)"
                                        (decrement)="removeQuantity(service, iterable.traveler.id)"
                                      />
                                    }
                                    @if (service.inputType !== 'number') {
                                      <fcom-button
                                        (clickBtn)="label.click()"
                                        [attr.data-testid]="'select-button-' + service.globalId + '-button'"
                                        [removeFromNavigation]="true"
                                        [text]="service.buttonText$ | async"
                                        [disabled]="
                                          !!(
                                            passengersHaveOverlappingBundles$
                                            | async
                                            | finMap: iterable.traveler.id
                                          ) ||
                                          (service.paidQuantity !== 0 &&
                                            (service.totalPrice?.amount !== '0' ||
                                              service.subCategory !== SubCategory.SPECIAL_DIET)) ||
                                          (service.paxLevelQuotaReached$ | async)
                                        "
                                        [mode]="ButtonMode.FILL"
                                        [theme]="
                                          (service.selected$ | async)
                                            ? ButtonTheme.LIGHT_SECONDARY_OUTLINE
                                            : ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE
                                        "
                                        [icon]="
                                          (service.selected$ | async) === true
                                            ? {
                                                name: SvgLibraryIcon.CHECKMARK,
                                                position: IconPosition.LEFT,
                                              }
                                            : undefined
                                        "
                                      />
                                    }
                                  </div>
                                }
                              </div>

                              @if (
                                (service.quota$ | finMap: 'type' | async) === QuotaType.TOPPED &&
                                CONFIG[category]?.toppedQuotaKey
                              ) {
                                <fcom-notification class="ps-small-t" [theme]="NotificationTheme.ALERT">
                                  {{ CONFIG[category].toppedQuotaKey | finLocalization }}
                                </fcom-notification>
                              }
                              @if (
                                (service.quota$ | finMap: 'type' | async) === QuotaType.NO &&
                                CONFIG[category]?.noQuotaLeftKey
                              ) {
                                <fcom-notification class="ps-small-t" [theme]="NotificationTheme.WARNING">
                                  {{ CONFIG[category].noQuotaLeftKey | finLocalization }}
                                </fcom-notification>
                              }
                            </div>
                          </div>
                          @if (service.paxLevelQuotaReached$ | async) {
                            <fcom-notification
                              data-testid="pax-quota-notification"
                              class="ps-small border-top"
                              [theme]="NotificationTheme.WARNING"
                            >
                              {{ 'ancillaries.quota.paxQuotaReached' | finLocalization }}
                            </fcom-notification>
                          }
                          @if (
                            service.paidQuantity > 0 &&
                            (service.totalPrice?.amount !== '0' || service.subCategory !== SubCategory.SPECIAL_DIET) &&
                            service.inputType !== 'number'
                          ) {
                            <fcom-notification
                              class="ps-small border-top"
                              [attr.data-testid]="'service-notification-paid-' + service.globalId"
                            >
                              {{ CONFIG[category]?.alreadyPurchased | finLocalization }}
                            </fcom-notification>
                          }
                          @if (
                            passengersThatAreBlockedFromSelecting.includes(iterable.traveler.id) &&
                            CONFIG[category].blockSelectionNotification
                          ) {
                            <fcom-notification
                              data-testid="block-service-notification"
                              class="ps-small border-top"
                              [theme]="NotificationTheme.ALERT"
                            >
                              {{ CONFIG[category].blockSelectionNotification | finLocalization }}
                            </fcom-notification>
                          }

                          @if (
                            !passengersThatAreBlockedFromSelecting.includes(iterable.traveler.id) && service.quota$
                              | async;
                            as quota
                          ) {
                            @if (
                              quota.count !== undefined &&
                              quota.count < 9 &&
                              quota.type === QuotaType.FREE &&
                              CONFIG[category]?.quotaLimitKey
                            ) {
                              <fcom-notification class="ps-small border-top" [theme]="NotificationTheme.WARNING">
                                {{ CONFIG[category].quotaLimitKey | finLocalization: { quota: quota.count } }}
                              </fcom-notification>
                            }
                          }
                          @if (
                            (service.category === Category.MEAL && service.translations.description) ||
                            service.translations.ingredients
                          ) {
                            <fin-product-expand [title]="'ancillaries.meals.showDetails' | finLocalization">
                              <fin-meal-details [meal]="service" />
                            </fin-product-expand>
                          }
                          @if (
                            'ancillaries.' + service.category + '.' + service.variant + '.moreAbout.article.url'
                              | finHasLocalizationKey
                          ) {
                            <fin-product-expand
                              [title]="
                                'ancillaries.' + service.category + '.' + service.variant + '.moreAbout.title'
                                  | finLocalization
                              "
                            >
                              <cms-unstyled-fragment
                                [contentUrl$]="
                                  'ancillaries.' + service.category + '.' + service.variant + '.moreAbout.article.url'
                                    | finLocalization
                                    | finToObservable
                                "
                                [showTitle]="false"
                              />
                            </fin-product-expand>
                          }
                        </div>
                      }
                    }
                    @if (iterable.additionalInfoBlockNotification) {
                      <fcom-notification
                        class="ps-xlarge-t"
                        [theme]="NotificationTheme.INFO"
                        data-testid="additional-info-block-notification"
                      >
                        <span [innerHTML]="iterable.additionalInfoBlockNotification | finLocalization"></span>
                      </fcom-notification>
                    }
                    @if (iterable.additionalInfoBlockUrl | finLocalization; as contentUrl) {
                      <ng-container
                        [ngTemplateOutlet]="additionalInfoBlockUrl"
                        [ngTemplateOutletContext]="{ contentUrl$: contentUrl | finToObservable }"
                      />
                    }
                  </div>
                </div>
              </fin-tab>
            }
          </fin-tab-group>
        </div>
      }
    }

    <!-- CASE 3/3: SHOW NO-SERVICE MESSAGE -->
    @case (SelectionState.NO_SERVICES) {
      <div class="flex flex--basis0 flex--column">
        @for (notification of notifications; track notification.key; let last = $last) {
          <fcom-notification
            data-testid="no-service-notification"
            [class.pr-medium-b]="last"
            [class.pr-xxsmall-b]="!last"
            [theme]="notification.theme"
            [iconName]="notification.iconName"
          >
            @if (notification.key | finHasLocalizationKey) {
              <span [innerHTML]="notification.key | finLocalization | finSafe: 'html'"></span>
            } @else {
              <span [innerHTML]="'services.cannotAdd' | finLocalization | finSafe: 'html'"></span>
            }

            @if (notification.reason) {
              <span [innerHTML]="notification.reason.key | finLocalization: notification.reason.data"></span>
            }
          </fcom-notification>
        }
      </div>
    }
  }
</div>

<ng-template #additionalInfoBlockUrl let-contentUrl$="contentUrl$">
  <section class="service-description mr-medium-t" data-testid="common-product-service-description">
    <cms-unstyled-fragment [contentUrl$]="contentUrl$" class="block" />
  </section>
</ng-template>
